import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * WorkerLanguageContext — shared bilingual EN/ES preference for the worker experience.
 * - Persisted to localStorage('worker_funnel_lang') so the funnel + dashboard share the choice.
 * - <html lang> updated automatically.
 * - Provides a `t(key)` helper that falls back to English when a string is missing.
 */
const LangContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => k, dict: {} });

const DICT = {
  en: {
    // Worker Dashboard
    welcome_back: 'Welcome back',
    find_subtitle: 'Find your next construction opportunity',
    your_dashboard: 'Your Dashboard',
    find_jobs: 'Find Jobs',
    my_applications: 'My Applications',
    messages: 'Messages',
    wallet: 'Wallet',
    my_wallet: 'My Wallet',
    time_clock: 'Time Clock',
    availability: 'Availability',
    referrals: 'Refer & Earn',
    profile: 'Profile',
    settings: 'Settings',
    apps_this_month: 'Job Applications This Month',
    restart_tour: 'Restart Tour',
    // Stats
    active_apps: 'Active Applications',
    interviews: 'Interviews',
    hired: 'Hired Jobs',
    earnings: 'Total Earnings',
    profile_views: 'Profile Views',
    rating: 'Rating',
    // Sections
    recommended_jobs: 'Recommended Jobs',
    recent_applications: 'Recent Applications',
    upcoming_work: 'Upcoming Work',
    no_jobs_yet: 'No matching jobs right now — check back soon.',
    no_apps_yet: "You haven't applied to any jobs yet.",
    view_all: 'View All',
    apply: 'Apply',
    applied: 'Applied',
    // Voice Translator hero
    vt_pill: 'English ↔ Spanish · Voice Translator',
    vt_title: 'Hable inglés o español — traducción instantánea',
    vt_body: 'Talk to your foreman or crew in either language. Tap, speak, and hear the translation.',
    vt_cta: 'Open',
    // Public profile banner
    pp_label: 'Your Public Profile is Live',
    pp_view: 'View',
    pp_copy: 'Copy',
    pp_share: 'Share',
    // Tool Groups
    group_work_jobs: 'Work & Jobs',
    group_payments: 'Payments & Finance',
    group_schedule: 'Schedule & Tools',
    saved_jobs: 'Saved Jobs',
    map_view: 'Map View',
    my_teams: 'My Teams',
    portfolio: 'Portfolio',
    my_availability: 'My Availability',
    refer_earn: 'Refer & Earn',
    escrow: 'Escrow',
    invoices: 'Invoices',
    reports: 'Reports',
    calendar: 'Calendar',
    timesheets: 'Timesheets',
    job_alerts: 'Job Alerts',
    documents: 'Documents',
    help: 'Help',
    // Stats
    applied_jobs: 'Applied Jobs',
    pending: 'pending',
    active_jobs: 'Active Jobs',
    in_progress: 'In progress',
    earnings_label: 'Earnings',
    this_month: 'This month',
    rating_label: 'Rating',
    reviews: 'reviews',
    // Recommended jobs section
    no_jobs_available: 'No jobs available',
    browse_all_jobs: 'Browse All Jobs',
    company: 'Company',
    view_details: 'View Details',
    // Sidebar
    profile_strength: 'Profile Strength',
    completion: 'Completion',
    profile_photo_added: 'Profile photo added',
    skills_listed: 'Skills listed',
    add_portfolio_items: 'Add portfolio items',
    complete_profile: 'Complete Profile',
    quick_links: 'Quick Links',
    escrow_dashboard: 'Escrow Dashboard',
    // Misc
    loading: 'Loading...',
    error: 'Error',
    language: 'Language',
    // BrowseJobs
    browse_jobs_title: 'Browse Jobs',
    browse_jobs_sub: 'Find your next construction opportunity',
    search_placeholder: 'Search by title, skill, or company...',
    filters: 'Filters',
    clear_filters: 'Clear filters',
    location: 'Location',
    any_location: 'Any location',
    budget_min: 'Min budget',
    budget_max: 'Max budget',
    job_type: 'Job type',
    skills: 'Skills',
    duration: 'Duration',
    posted: 'Posted',
    sort_by: 'Sort by',
    newest: 'Newest',
    oldest: 'Oldest',
    budget_high_low: 'Budget: High to Low',
    budget_low_high: 'Budget: Low to High',
    jobs_found: 'jobs found',
    no_results: 'No jobs match your filters',
    try_clear: 'Try clearing some filters or expanding your search radius.',
    save_job: 'Save job',
    saved: 'Saved',
    view_job: 'View job',
    // MyApplications
    my_apps_title: 'My Applications',
    my_apps_sub: 'Track every job you have applied to',
    all: 'All',
    pending_status: 'Pending',
    interview: 'Interview',
    accepted: 'Accepted',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn',
    no_apps_in_view: 'No applications in this view',
    apply_more_jobs: 'Apply to more jobs',
    withdraw_app: 'Withdraw application',
    applied_on: 'Applied on',
    // WorkerAvailability
    my_availability_title: 'My Availability',
    your_status: 'Your status',
    available_today: 'Available today',
    days_blocked_30: 'days blocked in next 30',
    vacation_mode: 'Vacation Mode',
    going_on_vacation: 'Going on vacation',
    vacation_until: 'Vacation until',
    private_note: 'Private note (optional)',
    note_placeholder: 'Just for your own reference',
    not_visible_contractors: 'not visible to contractors',
    save_availability: 'Save Availability',
    available_legend: 'Available',
    blocked_legend: 'Blocked',
    vacation_legend: 'Vacation',
    today_legend: 'Today',
    block_3_days: 'Block next 3 days',
    block_7_days: 'Block next 7 days',
    clear_all_blocked: 'Clear all blocked',
    // WorkerControlPanel — tabs + headers
    control_panel: 'Control Panel',
    tab_profile: 'Profile',
    tab_skills: 'Skills',
    tab_availability: 'Availability',
    tab_earnings: 'Earnings',
    tab_documents: 'Documents',
    tab_settings: 'Settings',
    tab_alerts: 'Alerts',
    overview: 'Overview',
    security: 'Security',
    back: 'Back',
    save_changes: 'Save changes',
    saving: 'Saving...',
    add_skill: 'Add Skill',
    earnings_overview: 'Earnings Overview',
    from_released_escrows: 'From released escrows',
    notifications: 'Notifications',
    email_notifications: 'Email notifications',
    sms_notifications: 'SMS notifications',
    push_notifications: 'Push notifications',
  },
  es: {
    welcome_back: 'Bienvenido de nuevo',
    find_subtitle: 'Encuentra tu próxima oportunidad de construcción',
    your_dashboard: 'Tu panel',
    find_jobs: 'Buscar trabajos',
    my_applications: 'Mis solicitudes',
    messages: 'Mensajes',
    wallet: 'Billetera',
    my_wallet: 'Mi billetera',
    time_clock: 'Reloj',
    availability: 'Disponibilidad',
    referrals: 'Refiere y gana',
    profile: 'Perfil',
    settings: 'Ajustes',
    apps_this_month: 'Solicitudes de trabajo este mes',
    restart_tour: 'Reiniciar tour',
    active_apps: 'Solicitudes activas',
    interviews: 'Entrevistas',
    hired: 'Trabajos contratados',
    earnings: 'Ganancias totales',
    profile_views: 'Vistas al perfil',
    rating: 'Calificación',
    recommended_jobs: 'Trabajos recomendados',
    recent_applications: 'Solicitudes recientes',
    upcoming_work: 'Trabajo próximo',
    no_jobs_yet: 'No hay trabajos compatibles ahora mismo — vuelve pronto.',
    no_apps_yet: 'Aún no has aplicado a ningún trabajo.',
    view_all: 'Ver todo',
    apply: 'Aplicar',
    applied: 'Aplicado',
    vt_pill: 'Inglés ↔ Español · Traductor de voz',
    vt_title: 'Habla inglés o español — traducción instantánea',
    vt_body: 'Habla con tu capataz o tu equipo en cualquier idioma. Toca, habla y escucha la traducción.',
    vt_cta: 'Abrir',
    pp_label: 'Tu perfil público está activo',
    pp_view: 'Ver',
    pp_copy: 'Copiar',
    pp_share: 'Compartir',
    // Tool Groups
    group_work_jobs: 'Trabajo y empleos',
    group_payments: 'Pagos y finanzas',
    group_schedule: 'Horario y herramientas',
    saved_jobs: 'Trabajos guardados',
    map_view: 'Vista de mapa',
    my_teams: 'Mis equipos',
    portfolio: 'Portafolio',
    my_availability: 'Mi disponibilidad',
    refer_earn: 'Refiere y gana',
    escrow: 'Depósito en garantía',
    invoices: 'Facturas',
    reports: 'Reportes',
    calendar: 'Calendario',
    timesheets: 'Hojas de tiempo',
    job_alerts: 'Alertas de trabajo',
    documents: 'Documentos',
    help: 'Ayuda',
    // Stats
    applied_jobs: 'Trabajos aplicados',
    pending: 'pendiente',
    active_jobs: 'Trabajos activos',
    in_progress: 'En progreso',
    earnings_label: 'Ganancias',
    this_month: 'Este mes',
    rating_label: 'Calificación',
    reviews: 'reseñas',
    // Recommended jobs section
    no_jobs_available: 'No hay trabajos disponibles',
    browse_all_jobs: 'Ver todos los trabajos',
    company: 'Empresa',
    view_details: 'Ver detalles',
    // Sidebar
    profile_strength: 'Fortaleza del perfil',
    completion: 'Progreso',
    profile_photo_added: 'Foto de perfil añadida',
    skills_listed: 'Habilidades listadas',
    add_portfolio_items: 'Agregar al portafolio',
    complete_profile: 'Completar perfil',
    quick_links: 'Enlaces rápidos',
    escrow_dashboard: 'Panel de depósito en garantía',
    loading: 'Cargando...',
    error: 'Error',
    language: 'Idioma',
    // BrowseJobs
    browse_jobs_title: 'Buscar trabajos',
    browse_jobs_sub: 'Encuentra tu próxima oportunidad de construcción',
    search_placeholder: 'Buscar por título, habilidad o empresa...',
    filters: 'Filtros',
    clear_filters: 'Limpiar filtros',
    location: 'Ubicación',
    any_location: 'Cualquier ubicación',
    budget_min: 'Presupuesto mín.',
    budget_max: 'Presupuesto máx.',
    job_type: 'Tipo de trabajo',
    skills: 'Habilidades',
    duration: 'Duración',
    posted: 'Publicado',
    sort_by: 'Ordenar por',
    newest: 'Más reciente',
    oldest: 'Más antiguo',
    budget_high_low: 'Presupuesto: Mayor a menor',
    budget_low_high: 'Presupuesto: Menor a mayor',
    jobs_found: 'trabajos encontrados',
    no_results: 'No hay trabajos que coincidan con tus filtros',
    try_clear: 'Prueba limpiar algunos filtros o ampliar el radio de búsqueda.',
    save_job: 'Guardar trabajo',
    saved: 'Guardado',
    view_job: 'Ver trabajo',
    // MyApplications
    my_apps_title: 'Mis solicitudes',
    my_apps_sub: 'Sigue cada trabajo al que has aplicado',
    all: 'Todas',
    pending_status: 'Pendiente',
    interview: 'Entrevista',
    accepted: 'Aceptada',
    rejected: 'Rechazada',
    withdrawn: 'Retirada',
    no_apps_in_view: 'No hay solicitudes en esta vista',
    apply_more_jobs: 'Aplicar a más trabajos',
    withdraw_app: 'Retirar solicitud',
    applied_on: 'Aplicada el',
    // WorkerAvailability
    my_availability_title: 'Mi disponibilidad',
    your_status: 'Tu estado',
    available_today: 'Disponible hoy',
    days_blocked_30: 'días bloqueados en los próximos 30',
    vacation_mode: 'Modo vacaciones',
    going_on_vacation: 'Tomarás vacaciones',
    vacation_until: 'Vacaciones hasta',
    private_note: 'Nota privada (opcional)',
    note_placeholder: 'Sólo para tu referencia',
    not_visible_contractors: 'no visible para contratistas',
    save_availability: 'Guardar disponibilidad',
    available_legend: 'Disponible',
    blocked_legend: 'Bloqueado',
    vacation_legend: 'Vacaciones',
    today_legend: 'Hoy',
    block_3_days: 'Bloquear próximos 3 días',
    block_7_days: 'Bloquear próximos 7 días',
    clear_all_blocked: 'Quitar todos los bloqueos',
    // WorkerControlPanel
    control_panel: 'Panel de control',
    tab_profile: 'Perfil',
    tab_skills: 'Habilidades',
    tab_availability: 'Disponibilidad',
    tab_earnings: 'Ganancias',
    tab_documents: 'Documentos',
    tab_settings: 'Ajustes',
    tab_alerts: 'Alertas',
    overview: 'Resumen',
    security: 'Seguridad',
    back: 'Atrás',
    save_changes: 'Guardar cambios',
    saving: 'Guardando...',
    add_skill: 'Agregar habilidad',
    earnings_overview: 'Resumen de ganancias',
    from_released_escrows: 'De depósitos liberados',
    notifications: 'Notificaciones',
    email_notifications: 'Notificaciones por correo',
    sms_notifications: 'Notificaciones SMS',
    push_notifications: 'Notificaciones push',
  },
};

export function WorkerLanguageProvider({ children }) {
  const SUPPORTED = ['en', 'es'];
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem('worker_funnel_lang');
      if (['en', 'es'].includes(stored)) return stored;
      // First visit: auto-detect from browser language
      const browser = (navigator.language || navigator.languages?.[0] || 'en').slice(0, 2).toLowerCase();
      return ['en', 'es'].includes(browser) ? browser : 'en';
    } catch { return 'en'; }
  });

  const setLang = useCallback((next) => {
    const normalized = SUPPORTED.includes(next) ? next : 'en';
    setLangState(normalized);
    try { localStorage.setItem('worker_funnel_lang', normalized); } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Listen for cross-tab changes (e.g., toggled in funnel, dashboard updates immediately)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'worker_funnel_lang' && e.newValue) {
        setLangState(['en', 'es'].includes(e.newValue) ? e.newValue : 'en');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const dict = DICT[lang] || DICT.en;
  const t = useCallback((key) => dict[key] ?? DICT.en[key] ?? key, [dict]);

  return (
    <LangContext.Provider value={{ lang, setLang, t, dict }}>
      {children}
    </LangContext.Provider>
  );
}

export function useWorkerLang() {
  return useContext(LangContext);
}
